/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import {
  CloudAccountCloudProvider,
  CloudAccountStatus,
} from "~/generated/graphql";
import { dateFormat } from "~/constants/dateFormats";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import {
  getCountBasedDisplayValue,
  removeSelectedValue,
} from "~/hooks/useAppliedFilters/utils";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { AccountQuery } from "../types/AccountQuery";

type DefinitionKey = "dateAdded" | "cloudProviders" | "cloudAccountStatuses";
type AccountQueryState = AccountQuery & QueryState;

type Definition = AppliedFilterDefinition<AccountQueryState, DefinitionKey>;

const cloudProviderLabelsByValue: Record<string, string> = {
  [CloudAccountCloudProvider.Aws]: "AWS",
  [CloudAccountCloudProvider.Azure]: "Azure",
  [CloudAccountCloudProvider.Gc]: "Google Cloud",
  [CloudAccountCloudProvider.Ibm]: "IBM Cloud",
};

const cloudAccountStatusLabelsByValue: Record<string, string> = {
  [CloudAccountStatus.AccessError]: "Access Error",
  [CloudAccountStatus.AccessMissing]: "Missing Credentials",
  [CloudAccountStatus.AccessOk]: "Connected",
  [CloudAccountStatus.InProgress]: "In Progress",
  [CloudAccountStatus.RegionsUnavailable]: "Unavailable Regions",
};

export const accountAppliedFilters: Definition[] = [
  {
    key: "dateAdded",
    label: "Date Added",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.startDateUTC && queryState.endDateUTC
        ? [queryState.startDateUTC, queryState.endDateUTC]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.startDateUTC).format(dateFormat.shortDate)} - ${dayjs(
        queryState.endDateUTC
      ).format(dateFormat.shortDate)}`,
    clear: () => ({
      startDateUTC: undefined,
      endDateUTC: undefined,
    }),
  },
  {
    key: "cloudProviders",
    label: "Cloud Provider",
    getValues: (queryState) => queryState.cloudProviders ?? [],
    getValueLabel: (value) => cloudProviderLabelsByValue[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = cloudProviderLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeCloudProviders ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeCloudProviders,
        {
          singular: "Cloud Provider",
          plural: "Cloud Providers",
        }
      );
    },
    clear: () => ({
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.cloudProviders,
        valueToRemove
      );
      return {
        cloudProviders: nextValues.length ? nextValues : undefined,
        excludeCloudProviders: nextValues.length
          ? queryState.excludeCloudProviders
          : undefined,
      };
    },
  },
  {
    key: "cloudAccountStatuses",
    label: "Account Status",
    getValues: (queryState) => queryState.cloudAccountStatuses ?? [],
    getValueLabel: (value) => cloudAccountStatusLabelsByValue[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = cloudAccountStatusLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeCloudAccountStatuses
          ? `Exclude: ${label}`
          : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeCloudAccountStatuses,
        {
          singular: "Account Status",
          plural: "Account Statuses",
        }
      );
    },
    clear: () => ({
      cloudAccountStatuses: undefined,
      excludeCloudAccountStatuses: undefined,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.cloudAccountStatuses,
        valueToRemove
      );
      return {
        cloudAccountStatuses: nextValues.length ? nextValues : undefined,
        excludeCloudAccountStatuses: nextValues.length
          ? queryState.excludeCloudAccountStatuses
          : undefined,
      };
    },
  },
];
